import React, { useState, useEffect, useRef } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getMyProjects, getProjectMilestones } from '../../services/projectService';
import { getClassesByProject, createClass, createRecurringClasses, logClassSummary, cancelClass, rescheduleClass, requestDurationOverride } from '../../services/classService';
import { formatDate, formatTime } from '../../utils/helpers';
import useForm from '../../hooks/useForm';
import { useToast } from '../../context/ToastContext';
import styles from './MentorClasses.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

const MentorClasses = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [currentProject, setCurrentProject] = useState(null);
  const [classes, setClasses] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRecurring, setIsRecurring] = useState(false);
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState(''); // Separate state for milestone dropdown
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleValues, setRescheduleValues] = useState({ new_date: '', new_time: '', duration_minutes: '', meet_link: '' });
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [durationTarget, setDurationTarget] = useState(null);
  const [durationValues, setDurationValues] = useState({ manual_duration_minutes: '', reason: '' });
  const [isSubmittingDuration, setIsSubmittingDuration] = useState(false);
  const toast = useToast();

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  const getErrorMessage = (error, fallback = 'Something went wrong') => {
    return error?.response?.data?.detail || error?.message || fallback;
  };

  const isProjectFrozen = () => {
    return currentProject && (currentProject.status === 'on_hold' || currentProject.status === 'cancelled');
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (selectedProject) {
      fetchClasses(selectedProject);
      fetchMilestones(selectedProject);
      const project = projects.find(p => p.id === selectedProject);
      setCurrentProject(project);

      pollIntervalRef.current = setInterval(() => {
        fetchClasses(selectedProject);
      }, 30000); // refresh every 30 s
    } else {
      setCurrentProject(null);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [selectedProject, projects]);

  const fetchProjects = async () => {
    try {
      const data = await getMyProjects();
      setProjects(data);
      // Don't auto-select first project - let mentor choose
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async (projectId) => {
    try {
      const data = await getClassesByProject(projectId);
      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchMilestones = async (projectId) => {
    try {
      const data = await getProjectMilestones(projectId);
      setMilestones(data);
    } catch (error) {
      console.error('Error fetching milestones:', error);
    }
  };

  const { values, handleChange, handleSubmit, resetForm, isSubmitting: isSubmittingCreate } = useForm(
    {
      class_number: 0, // 0 means auto-calculate based on date
      class_date: '',
      class_time: '',
      duration: 60,
      meet_link: ''
    },
    async (formValues) => {
      try {
        await createClass({ ...formValues, project_id: selectedProject });
        setShowCreateModal(false);
        resetForm();
        setIsRecurring(false);
        fetchClasses(selectedProject);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Error creating class'));
      }
    }
  );

  const {
    values: recurringValues,
    handleChange: handleRecurringChange,
    handleSubmit: handleRecurringSubmit,
    resetForm: resetRecurringForm,
    isSubmitting: isSubmittingRecurring
  } = useForm(
    {
      start_date: '',
      end_date: '',
      class_time: '',
      duration: 60,
      meet_link: ''
    },
    async (formValues) => {
      try {
        if (selectedWeekdays.length === 0) {
          toast.warning('Please select at least one weekday');
          return;
        }

        const createdClasses = await createRecurringClasses({
          ...formValues,
          project_id: selectedProject,
          weekdays: selectedWeekdays
        });
        toast.success(`Successfully created ${createdClasses.length} recurring classes!`);
        setShowCreateModal(false);
        resetRecurringForm();
        setIsRecurring(false);
        setSelectedWeekdays([]);
        fetchClasses(selectedProject);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Error creating recurring classes'));
      }
    }
  );

  const { values: logValues, handleChange: handleLogChange, handleSubmit: handleLogSubmit, setValues: setLogValues, isSubmitting: isSubmittingLog } = useForm(
    {
      topics_covered: [],
      class_summary: '',
      resources_shared: [],
      mentor_attendance: true,
      student_attendance: false,
      bot_attendance: false,
      milestone_id: '',
      milestone_item_index: null
    },
    async (formValues) => {
      try {
        // Clean up milestone fields - send null instead of empty string for UUID fields
        const cleanedData = {
          ...formValues,
          milestone_id: formValues.milestone_id || null,
          milestone_item_index: formValues.milestone_item_index !== null && formValues.milestone_item_index !== undefined
            ? formValues.milestone_item_index
            : null
        };
        await logClassSummary(selectedClass.id, cleanedData);
        setShowLogModal(false);
        fetchClasses(selectedProject);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Error logging class'));
      }
    }
  );

  const handleLogClass = (cls) => {
    console.log('Opening log modal for class:', cls);
    console.log('Available milestones:', milestones);
    setSelectedClass(cls);

    // Set milestone dropdown state - use ||| as separator to avoid conflicts with UUID dashes
    const milestoneValue = (cls.milestone_id && cls.milestone_item_index !== null && cls.milestone_item_index !== undefined)
      ? `${cls.milestone_id}|||${cls.milestone_item_index}`
      : '';
    console.log('Setting milestone dropdown to:', milestoneValue);
    setSelectedMilestone(milestoneValue);

    const initialValues = {
      topics_covered: cls.topics_covered || [],
      class_summary: cls.class_summary || '',
      resources_shared: cls.resources_shared || [],
      mentor_attendance: cls.mentor_attendance,
      student_attendance: cls.student_attendance,
      milestone_id: cls.milestone_id || '',
      milestone_item_index: cls.milestone_item_index !== undefined ? cls.milestone_item_index : null
    };
    console.log('Setting initial log values:', initialValues);
    setLogValues(initialValues);
    setShowLogModal(true);
  };

  const handleCancelClass = (cls) => {
    setCancelTarget(cls);
    setShowCancelModal(true);
  };

  const handleRescheduleClass = (cls) => {
    setRescheduleTarget(cls);
    setRescheduleValues({
      new_date: cls.class_date,
      new_time: typeof cls.class_time === 'string' ? cls.class_time.substring(0, 5) : '',
      duration_minutes: cls.duration || 60,
      meet_link: cls.meet_link || '',
    });
    setShowRescheduleModal(true);
  };

  const confirmReschedule = async () => {
    if (!rescheduleTarget || !rescheduleValues.new_date || !rescheduleValues.new_time) {
      toast.warning('Please fill in the required date and time fields');
      return;
    }
    setIsRescheduling(true);
    try {
      const payload = {
        new_date: rescheduleValues.new_date,
        new_time: rescheduleValues.new_time,
      };
      if (rescheduleValues.duration_minutes) payload.duration_minutes = parseInt(rescheduleValues.duration_minutes);
      if (rescheduleValues.meet_link) payload.meet_link = rescheduleValues.meet_link;
      await rescheduleClass(rescheduleTarget.id, payload);
      toast.success('Class rescheduled successfully');
      setShowRescheduleModal(false);
      setRescheduleTarget(null);
      fetchClasses(selectedProject);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error rescheduling class'));
    } finally {
      setIsRescheduling(false);
    }
  };

  const confirmCancel = async (cancelSeries = false) => {
    if (!cancelTarget) return;
    setIsCancelling(true);
    try {
      await cancelClass(cancelTarget.id, cancelSeries);
      toast.success('Class cancelled successfully');
      setShowCancelModal(false);
      setCancelTarget(null);
      fetchClasses(selectedProject);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error cancelling class'));
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRequestDuration = (cls) => {
    setDurationTarget(cls);
    setDurationValues({ manual_duration_minutes: cls.duration || '', reason: '' });
    setShowDurationModal(true);
  };

  const confirmDurationOverride = async () => {
    if (!durationValues.manual_duration_minutes || !durationValues.reason.trim()) {
      toast.warning('Please enter the duration and a reason');
      return;
    }
    setIsSubmittingDuration(true);
    try {
      await requestDurationOverride(durationTarget.id, {
        manual_duration_minutes: parseInt(durationValues.manual_duration_minutes),
        reason: durationValues.reason,
      });
      toast.success('Duration review requested — a super admin will review it');
      setShowDurationModal(false);
      setDurationTarget(null);
      fetchClasses(selectedProject);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error requesting duration review'));
    } finally {
      setIsSubmittingDuration(false);
    }
  };

  const handleWeekdayToggle = (weekdayIndex) => {
    setSelectedWeekdays(prev => {
      if (prev.includes(weekdayIndex)) {
        return prev.filter(day => day !== weekdayIndex);
      } else {
        return [...prev, weekdayIndex];
      }
    });
  };

  const weekdays = [
    { label: 'Monday', value: 0 },
    { label: 'Tuesday', value: 1 },
    { label: 'Wednesday', value: 2 },
    { label: 'Thursday', value: 3 },
    { label: 'Friday', value: 4 },
    { label: 'Saturday', value: 5 },
    { label: 'Sunday', value: 6 }
  ];

  const getClassStatus = (cls) => {
    const classDate = new Date(cls.class_date);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    classDate.setHours(0, 0, 0, 0);

    const isPast = classDate < currentDate;

    if (!isPast) {
      return { color: 'empty', icon: '○', label: 'Upcoming' }; // Empty circle
    }

    // Class date has passed
    if (cls.mentor_attendance && cls.student_attendance) {
      return { color: 'green', icon: '✓', label: 'Both attended' };
    } else if (cls.mentor_attendance && !cls.student_attendance) {
      return { color: 'blue', icon: '✓', label: 'Only mentor attended' };
    } else if (!cls.mentor_attendance && cls.student_attendance) {
      return { color: 'orange', icon: '✓', label: 'Only student attended' };
    } else {
      return { color: 'grey', icon: '✓', label: 'Class held (not logged)' };
    }
  };

  const isClassPast = (classDate) => {
    const cls = new Date(classDate);
    const current = new Date();
    current.setHours(0, 0, 0, 0);
    cls.setHours(0, 0, 0, 0);
    return cls < current;
  };

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.classesPage}>
      <div className={styles.header}>
        <div>
          <h1>Classes</h1>
          <p>Schedule, reschedule, and log project sessions.</p>
        </div>
        <Button
          icon="+"
          onClick={() => setShowCreateModal(true)}
          disabled={!selectedProject || isProjectFrozen()}
          className={styles.createButton}
        >
          Create Class
        </Button>
      </div>

      {isProjectFrozen() && (
        <Card>
          <p style={{ color: '#F44336', fontWeight: 'bold', textAlign: 'center' }}>
            Alert: This project is {currentProject.status === 'on_hold' ? 'ON HOLD' : 'CANCELLED'}.
            You cannot create or modify classes.
          </p>
        </Card>
      )}

      <div className={styles.filterSection}>
        <div className={`${styles.formGroup} ${styles.projectPicker}`}>
          <label>Select Project</label>
          <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className={styles.select}>
            <option value="">-- Select a Project --</option>
            {sortProjectsByName(projects).map(project => (
              <option key={project.id} value={project.id}>
                {project.project_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedProject ? (
        <div className={styles.classesGrid}>
          {classes.length === 0 ? (
            <Card>
              <p className={styles.empty}>No classes scheduled yet</p>
            </Card>
          ) : (
            classes.map(cls => {
              const status = getClassStatus(cls);
              const isPast = isClassPast(cls.class_date);

              return (
                <Card
                  key={cls.id}
                  hover
                  className={`${styles.classCard} ${isPast ? styles.pastClass : ''}`}
                >
                  <div className={styles.classHeader}>
                    <div className={styles.classTitle}>
                      <span
                        className={`${styles.statusIndicator} ${styles[`status${status.color.charAt(0).toUpperCase() + status.color.slice(1)}`]}`}
                        title={status.label}
                      >
                        {status.icon}
                      </span>
                      <div>
                        <h3>Class {cls.class_number}</h3>
                        <span>{status.label}</span>
                      </div>
                    </div>
                    <span className={styles.duration}>{cls.duration} min</span>
                  </div>
                  <div className={styles.classDetails}>
                    <div className={styles.detailGrid}>
                      <div>
                        <span className={styles.detailLabel}>Date</span>
                        <strong>{formatDate(cls.class_date)}</strong>
                      </div>
                      <div>
                        <span className={styles.detailLabel}>Time</span>
                        <strong>{formatTime(cls.class_time)}</strong>
                      </div>
                    </div>
                    {cls.meet_link && (
                      isPast ? (
                        <span className={`${styles.meetLink} ${styles.meetLinkDisabled}`}>
                           Join Meet
                        </span>
                      ) : (
                        <a
                          href={cls.meet_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.meetLink}
                        >
                           Join Meet
                        </a>
                      )
                    )}
                    <div className={styles.attendance}>
                      <span>Mentor: {cls.mentor_attendance ? 'Yes' : 'No'}</span>
                      <span>Student: {cls.student_attendance ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                  <div className={styles.statusPills}>
                    {cls.status === 'cancelled' && (
                      <span className={`${styles.statusPill} ${styles.cancelledPill}`}>
                        Cancelled
                      </span>
                    )}
                    {cls.sync_source === 'sync' && (
                      <span className={`${styles.statusPill} ${styles.whatsappPill}`}>
                        From WhatsApp
                      </span>
                    )}
                    {cls.sync_meeting_id && cls.sync_source !== 'sync' && (
                      <span className={`${styles.statusPill} ${styles.syncedPill}`}>
                        Synced
                      </span>
                    )}
                    {cls.duration_override_status === 'pending' && (
                      <span className={`${styles.statusPill} ${styles.whatsappPill}`}>
                        Duration Review Pending
                      </span>
                    )}
                    {cls.duration_override_status === 'approved' && (
                      <span className={`${styles.statusPill} ${styles.syncedPill}`}>
                        Duration Confirmed: {cls.actual_duration_minutes} min
                      </span>
                    )}
                    {cls.duration_override_status === 'rejected' && (
                      <span className={`${styles.statusPill} ${styles.cancelledPill}`} title={cls.duration_override_feedback || ''}>
                        Duration Review Rejected
                      </span>
                    )}
                  </div>
                  <div className={styles.classActions}>
                    {isPast && cls.status !== 'cancelled' && !cls.actual_duration_minutes &&
                      (cls.duration_override_status === 'none' || cls.duration_override_status === 'rejected') && (
                      <Button
                        size="small"
                        variant="outline"
                        onClick={() => handleRequestDuration(cls)}
                        disabled={isProjectFrozen()}
                      >
                        Request Duration Review
                      </Button>
                    )}
                    <Button
                      size="small"
                      onClick={() => handleLogClass(cls)}
                      disabled={isProjectFrozen() || cls.status === 'cancelled'}
                      className={styles.logButton}
                    >
                      {cls.class_summary ? 'View/Edit Log' : 'Log Class'}
                    </Button>
                    {cls.status !== 'cancelled' && !isPast && (
                      <Button
                        size="small"
                        onClick={() => handleRescheduleClass(cls)}
                        disabled={isProjectFrozen()}
                        className={styles.rescheduleButton}
                      >
                        Reschedule
                      </Button>
                    )}
                    {cls.status !== 'cancelled' && !isPast && (
                      <Button
                        size="small"
                        onClick={() => handleCancelClass(cls)}
                        disabled={isProjectFrozen()}
                        className={styles.cancelButton}
                      >
                        Cancel Class
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })
          )}
        </div>
      ) : (
        <Card>
          <p className={styles.empty}>Please select a project to view classes</p>
        </Card>
      )}

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create New Class">
        <div className={styles.classTypeToggle}>
          <button
            className={!isRecurring ? styles.toggleActive : styles.toggleInactive}
            onClick={() => setIsRecurring(false)}
            type="button"
          >
            Single Class
          </button>
          <button
            className={isRecurring ? styles.toggleActive : styles.toggleInactive}
            onClick={() => setIsRecurring(true)}
            type="button"
          >
            Recurring Classes
          </button>
        </div>

        {!isRecurring ? (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.infoBox} style={{ background: '#fff3cd', borderLeftColor: '#ffc107' }}>
              <p style={{ color: '#856404' }}>Auto: Class number will be assigned automatically based on the date</p>
            </div>
            <div className={styles.formGroup}>
              <label>Date *</label>
              <input
                type="date"
                name="class_date"
                value={values.class_date}
                onChange={handleChange}
                min={today}
                required
              />
              <small style={{ color: '#666', fontSize: '12px' }}>Cannot create classes for past dates</small>
            </div>
            <div className={styles.formGroup}>
              <label>Time *</label>
              <input type="time" name="class_time" value={values.class_time} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Duration (minutes) *</label>
              <input type="number" name="duration" value={values.duration} onChange={handleChange} required />
            </div>
            {/* <div className={styles.formGroup}>
              <label>Google Meet Link</label>
              <input type="url" name="meet_link" value={values.meet_link} onChange={handleChange} placeholder="https://meet.google.com/xxx-xxxx-xxx" />
            </div> */}
            <div className={styles.formActions}>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button type="submit" loading={isSubmittingCreate}>Create Class</Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleRecurringSubmit} className={styles.form}>
            <div className={styles.infoBox}>
              <p>Schedule: Classes will be created on selected weekdays between the start and end dates.</p>
              <p>Auto: Class numbers will be assigned automatically starting from #{classes.length + 1}</p>
            </div>
            <div className={styles.formGroup}>
              <label>Start Date *</label>
              <input
                type="date"
                name="start_date"
                value={recurringValues.start_date}
                onChange={handleRecurringChange}
                min={today}
                required
              />
              <small style={{ color: '#666', fontSize: '12px' }}>Cannot create classes for past dates</small>
            </div>
            <div className={styles.formGroup}>
              <label>End Date *</label>
              <input
                type="date"
                name="end_date"
                value={recurringValues.end_date}
                onChange={handleRecurringChange}
                min={recurringValues.start_date || today}
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>Select Weekdays *</label>
              <div className={styles.weekdayGrid}>
                {weekdays.map(day => (
                  <label key={day.value} className={styles.weekdayCheckbox}>
                    <input
                      type="checkbox"
                      checked={selectedWeekdays.includes(day.value)}
                      onChange={() => handleWeekdayToggle(day.value)}
                    />
                    <span>{day.label}</span>
                  </label>
                ))}
              </div>
              {selectedWeekdays.length === 0 && (
                <small style={{ color: '#F44336', fontSize: '12px' }}>Please select at least one weekday</small>
              )}
            </div>
            <div className={styles.formGroup}>
              <label>Time *</label>
              <input type="time" name="class_time" value={recurringValues.class_time} onChange={handleRecurringChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Duration (minutes) *</label>
              <input type="number" name="duration" value={recurringValues.duration} onChange={handleRecurringChange} required />
            </div>
            {/* <div className={styles.formGroup}>
              <label>Google Meet Link</label>
              <input type="url" name="meet_link" value={recurringValues.meet_link} onChange={handleRecurringChange} placeholder="https://meet.google.com/xxx-xxxx-xxx" />
            </div> */}
            <div className={styles.formActions}>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button type="submit" loading={isSubmittingRecurring}>Create Recurring Classes</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={showLogModal} onClose={() => setShowLogModal(false)} title="Log Class Details">
        <form onSubmit={handleLogSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Link to Milestone Item</label>
            <select
              value={selectedMilestone}
              onChange={(e) => {
                const newValue = e.target.value;
                console.log('Dropdown changed to:', newValue);
                setSelectedMilestone(newValue);

                if (newValue) {
                  const [milestoneId, itemIndex] = newValue.split('|||');
                  console.log('Updating logValues with milestone:', milestoneId, 'index:', itemIndex);
                  setLogValues({
                    ...logValues,
                    milestone_id: milestoneId,
                    milestone_item_index: parseInt(itemIndex)
                  });
                } else {
                  console.log('Clearing milestone from logValues');
                  setLogValues({
                    ...logValues,
                    milestone_id: '',
                    milestone_item_index: null
                  });
                }
              }}
              className={styles.select}
            >
              <option value="">-- No milestone item --</option>
              {milestones.length > 0 ? (
                milestones.flatMap(milestone =>
                  (milestone.milestone_items && Array.isArray(milestone.milestone_items))
                    ? milestone.milestone_items.map((item, index) => (
                      <option
                        key={`${milestone.id}|||${index}`}
                        value={`${milestone.id}|||${index}`}
                      >
                        M{milestone.milestone_number} - {item}
                      </option>
                    ))
                    : []
                )
              ) : (
                <option disabled>No milestones available</option>
              )}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Class Summary</label>
            <textarea name="class_summary" value={logValues.class_summary} onChange={handleLogChange} rows="4" placeholder="What was covered in this class..." />
          </div>
          <div className={styles.formGroup}>
            <label>Topics Covered (comma-separated)</label>
            <input name="topics_covered" value={logValues.topics_covered.join(', ')} onChange={(e) => handleLogChange({ target: { name: 'topics_covered', value: e.target.value.split(',').map(s => s.trim()) } })} placeholder="Topic 1, Topic 2, Topic 3" />
          </div>
          <div className={styles.formGroup}>
            <label>Resources Shared (comma-separated URLs)</label>
            <input name="resources_shared" value={logValues.resources_shared.join(', ')} onChange={(e) => handleLogChange({ target: { name: 'resources_shared', value: e.target.value.split(',').map(s => s.trim()) } })} placeholder="https://..., https://..." />
          </div>
          <div className={styles.checkboxGroup}>
            <label>
              <input type="checkbox" name="mentor_attendance" checked={logValues.mentor_attendance} onChange={(e) => handleLogChange({ target: { name: 'mentor_attendance', value: e.target.checked } })} />
              Mentor Attended
            </label>
            <label>
              <input type="checkbox" name="student_attendance" checked={logValues.student_attendance} onChange={(e) => handleLogChange({ target: { name: 'student_attendance', value: e.target.checked } })} />
              Student Attended
            </label>
            <label>
              <input type="checkbox" name="bot_attendance" checked={logValues.bot_attendance} onChange={(e) => handleLogChange({ target: { name: 'bot_attendance', value: e.target.checked } })} />
              Bot Attended
            </label>
          </div>
          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setShowLogModal(false)}>Cancel</Button>
            <Button type="submit" loading={isSubmittingLog}>Save Log</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showCancelModal} onClose={() => !isCancelling && setShowCancelModal(false)} title="Cancel Class">
        <p>Are you sure you want to cancel <strong>Class {cancelTarget?.class_number}</strong> on {cancelTarget ? formatDate(cancelTarget.class_date) : ''}?</p>
        {cancelTarget?.sync_source === 'sync' && (
          <p style={{ color: '#E65100', marginTop: '8px', fontSize: '0.9em' }}>
            This session was scheduled via WhatsApp. Cancelling here will also cancel it in SYNC.
          </p>
        )}
        {cancelTarget?.parent_sync_meeting_id && (
          <p style={{ color: '#C62828', marginTop: '8px', fontSize: '0.9em' }}>
            This class is part of a recurring series. You can cancel just this session or the entire series.
          </p>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '20px', flexWrap: 'wrap' }}>
          <Button
            onClick={() => confirmCancel(false)}
            disabled={isCancelling}
            style={{ background: '#FFEBEE', color: '#C62828', border: '1px solid #EF9A9A' }}
          >
            {isCancelling ? 'Cancelling…' : 'Cancel This Class'}
          </Button>
          {cancelTarget?.parent_sync_meeting_id && (
            <Button
              onClick={() => confirmCancel(true)}
              disabled={isCancelling}
              style={{ background: '#C62828', color: '#fff' }}
            >
              Cancel Entire Series
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
            Keep Class
          </Button>
        </div>
      </Modal>

      <Modal isOpen={showRescheduleModal} onClose={() => !isRescheduling && setShowRescheduleModal(false)} title="Reschedule Class">
        <p style={{ marginBottom: '16px' }}>
          Rescheduling <strong>Class {rescheduleTarget?.class_number}</strong>.
          {rescheduleTarget?.sync_meeting_id && (
            <span style={{ color: '#1565C0', fontSize: '0.9em', display: 'block', marginTop: '4px' }}>
              SYNC will be notified automatically.
            </span>
          )}
        </p>
        <div className={styles.formGroup}>
          <label>New Date *</label>
          <input
            type="date"
            value={rescheduleValues.new_date}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => setRescheduleValues(v => ({ ...v, new_date: e.target.value }))}
          />
        </div>
        <div className={styles.formGroup}>
          <label>New Time *</label>
          <input
            type="time"
            value={rescheduleValues.new_time}
            onChange={(e) => setRescheduleValues(v => ({ ...v, new_time: e.target.value }))}
          />
        </div>
        <div className={styles.formGroup}>
          <label>Duration (minutes)</label>
          <input
            type="number"
            value={rescheduleValues.duration_minutes}
            onChange={(e) => setRescheduleValues(v => ({ ...v, duration_minutes: e.target.value }))}
          />
        </div>
        <div className={styles.formGroup}>
          <label>Google Meet Link</label>
          {rescheduleValues.meet_link ? (
            <div className={styles.meetLinkDisplayBox}>
              <span className={styles.meetIcon}></span>
              <a 
                href={rescheduleValues.meet_link} 
                target="_blank" 
                rel="noopener noreferrer" 
                className={styles.previewLink}
                title={rescheduleValues.meet_link}
              >
                {rescheduleValues.meet_link}
              </a>
              <button 
                type="button" 
                className={styles.copyBtn}
                onClick={() => {
                  navigator.clipboard.writeText(rescheduleValues.meet_link);
                  toast.success('Link copied to clipboard!');
                }}
              >
                Copy
              </button>
            </div>
          ) : (
            <span className={styles.noLinkText}>No meeting link set</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
          <Button
            onClick={confirmReschedule}
            disabled={isRescheduling}
            style={{ background: '#1565C0', color: '#fff' }}
          >
            {isRescheduling ? 'Rescheduling…' : 'Confirm Reschedule'}
          </Button>
          <Button variant="outline" onClick={() => setShowRescheduleModal(false)} disabled={isRescheduling}>
            Cancel
          </Button>
        </div>
      </Modal>

      <Modal isOpen={showDurationModal} onClose={() => !isSubmittingDuration && setShowDurationModal(false)} title="Request Duration Review">
        <p style={{ marginBottom: '16px' }}>
          No attendance-tracked duration was found for <strong>Class {durationTarget?.class_number}</strong>.
          Enter the actual minutes conducted and a reason (e.g. "Sembly bot failed to join") for a super admin to review.
        </p>
        <div className={styles.formGroup}>
          <label>Duration Conducted (minutes) *</label>
          <input
            type="number"
            min="1"
            value={durationValues.manual_duration_minutes}
            onChange={(e) => setDurationValues(v => ({ ...v, manual_duration_minutes: e.target.value }))}
          />
        </div>
        <div className={styles.formGroup}>
          <label>Reason *</label>
          <textarea
            rows="3"
            value={durationValues.reason}
            onChange={(e) => setDurationValues(v => ({ ...v, reason: e.target.value }))}
            placeholder="Sembly bot failed to join..."
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
          <Button onClick={confirmDurationOverride} disabled={isSubmittingDuration}>
            {isSubmittingDuration ? 'Submitting…' : 'Submit for Review'}
          </Button>
          <Button variant="outline" onClick={() => setShowDurationModal(false)} disabled={isSubmittingDuration}>
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default MentorClasses;
